import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, RefreshCw, Trash2, Inbox, ArrowLeft, Mail, Phone } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ["new", "read", "contacted", "archived"];
const STATUS_STYLES = {
  new: "bg-neon-cyan/15 text-neon-cyan border-neon-cyan/30",
  read: "bg-white/10 text-white/70 border-white/20",
  contacted: "bg-neon-purple/15 text-neon-purple border-neon-purple/30",
  archived: "bg-white/5 text-white/40 border-white/10",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Admin = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contact`);
      setRows(res.data || []);
    } catch (err) {
      toast.error("Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contact/${id}/status`, null, { params: { status } });
      setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success("Submission deleted.");
    } catch {
      toast.error("Could not delete submission.");
    }
  };

  const newCount = rows.filter((r) => r.status === "new").length;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-page">
      <div className="mx-auto max-w-[1440px] px-6 py-10 md:px-12">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <Link
              to="/"
              data-testid="admin-back-link"
              className="mb-3 inline-flex items-center gap-2 text-sm text-white/50 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" /> Back to site
            </Link>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-white">
              Contact Submissions
            </h1>
            <p className="mt-1 text-sm text-white/50">
              {rows.length} total · <span className="text-neon-cyan">{newCount} new</span>
            </p>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh-button"
            className="inline-flex items-center gap-2 self-start rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/10"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Content */}
        <div className="mt-8 overflow-hidden rounded-3xl border border-white/10 bg-ink-soft">
          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-white/40" />
            </div>
          ) : rows.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-center" data-testid="admin-empty">
              <Inbox className="h-10 w-10 text-white/20" />
              <p className="mt-4 font-heading text-lg text-white">No submissions yet</p>
              <p className="mt-1 text-sm text-white/50">
                New contact requests will appear here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="admin-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/50">Contact</TableHead>
                    <TableHead className="text-white/50">Project</TableHead>
                    <TableHead className="text-white/50">Details</TableHead>
                    <TableHead className="text-white/50">Received</TableHead>
                    <TableHead className="text-white/50">Status</TableHead>
                    <TableHead className="text-right text-white/50">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} className="border-white/10" data-testid={`admin-row-${r.id}`}>
                      <TableCell className="align-top">
                        <p className="font-medium text-white">{r.full_name}</p>
                        {r.business_name ? (
                          <p className="text-xs text-white/50">{r.business_name}</p>
                        ) : null}
                        <a href={`mailto:${r.email}`} className="mt-1 flex items-center gap-1 text-xs text-neon-cyan hover:underline">
                          <Mail className="h-3 w-3" /> {r.email}
                        </a>
                        {r.phone ? (
                          <span className="mt-0.5 flex items-center gap-1 text-xs text-white/50">
                            <Phone className="h-3 w-3" /> {r.phone}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="align-top">
                        <p className="text-sm text-white/80">{r.website_type}</p>
                        {r.budget ? (
                          <p className="text-xs text-white/45">{r.budget}</p>
                        ) : null}
                      </TableCell>
                      <TableCell className="max-w-xs align-top">
                        <p className="text-sm leading-relaxed text-white/60">
                          {r.project_details}
                        </p>
                      </TableCell>
                      <TableCell className="whitespace-nowrap align-top text-sm text-white/50">
                        {fmtDate(r.created_at)}
                      </TableCell>
                      <TableCell className="align-top">
                        <Badge variant="outline" className={`capitalize ${STATUS_STYLES[r.status] || ""}`}>
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="flex items-center justify-end gap-2">
                          <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                            <SelectTrigger
                              data-testid={`admin-status-select-${r.id}`}
                              className="h-9 w-[130px] rounded-lg border-white/10 bg-white/[0.03] text-xs text-white"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s} value={s} className="capitalize">
                                  {s}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <button
                            onClick={() => remove(r.id)}
                            data-testid={`admin-delete-${r.id}`}
                            aria-label="Delete submission"
                            className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-white/50 transition-colors hover:border-destructive/50 hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
